import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import Loader from "../layouts/components/Loader";
import toast from "react-hot-toast";
import "./AdminOrderDetailsPage.css";

const API_BASE = "https://api.mariamlyluxe.com/public/api";

export default function AdminOrderDetailsPage() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusUpdateModal, setStatusUpdateModal] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    const userRole = localStorage.getItem("user_role");

    if (!token || userRole !== "1") {
      navigate("/");
      return;
    }

    if (orderId) {
      fetchOrderDetails(token);
    }
  }, [navigate, orderId]);

  const fetchOrderDetails = async (token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Handle different response structures
        if (data.success && data.order) {
          // Single order response structure
          setOrder(data.order);
        } else if (data.success && data.orders && data.orders.data) {
          // Paginated response structure
          const orderData = Array.isArray(data.orders.data) ? data.orders.data[0] : data.orders.data;
          setOrder(orderData);
        } else if (data.data) {
          // Alternative response structure
          setOrder(Array.isArray(data.data) ? data.data[0] : data.data);
        } else {
          setOrder(data);
        }
      } else {
        toast.error("Failed to fetch order details");
        navigate("/admin/orders");
      }
    } catch (error) {
      toast.error("Error fetching order details");
      console.error("Error:", error);
      navigate("/admin/orders");
    } finally {
      setLoading(false);
    }
  };

  

  const updateOrderStatus = async () => {
    if (!newStatus) {
      toast.error("Please select a status");
      return;
    }

    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    setIsUpdatingStatus(true);

    try {
      const response = await fetch(`${API_BASE}/orders/${orderId}/update-status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          order_status: newStatus
        })
      });

      if (response.ok) {
        toast.success("Order status updated successfully");
        setStatusUpdateModal(false);
        setNewStatus("");
        fetchOrderDetails(token);
      } else {
        toast.error("Failed to update order status");
      }
    } catch (error) {
      toast.error("Error updating order status");
      console.error("Error:", error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const printPackagingList = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Packaging List - Order #${order.order_id}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .logo {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .order-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 30px;
            }
            .order-details, .customer-details {
              flex: 1;
              margin: 0 10px;
            }
            .section-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 10px;
              border-bottom: 1px solid #ccc;
              padding-bottom: 5px;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            .items-table th, .items-table td {
              border: 1px solid #333;
              padding: 10px;
              text-align: left;
            }
            .items-table th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .item-image {
              width: 60px;
              height: 60px;
              object-fit: cover;
              border-radius: 4px;
            }
            .image-placeholder {
              width: 60px;
              height: 60px;
              background-color: #f0f0f0;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 4px;
              font-size: 12px;
              color: #666;
            }
            .quantity-cell {
              text-align: center;
              font-weight: bold;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">Mariamly Luxe</div>
            <h1>Packaging List</h1>
            <p>Order #${order.order_id}</p>
          </div>
          
          <div class="order-info">
            <div class="order-details">
              <div class="section-title">Order Details</div>
              <p><strong>Order ID:</strong> ${order.order_id}</p>
              <p><strong>Date:</strong> ${new Date(order.date_added).toLocaleDateString()}</p>
              <p><strong>Status:</strong> ${order.order_status}</p>
              <p><strong>Total Items:</strong> ${order.order_products ? order.order_products.reduce((sum, item) => sum + item.quantity, 0) : 0}</p>
            </div>
            
            <div class="customer-details">
              <div class="section-title">Customer Details</div>
              <p><strong>Name:</strong> ${order.user ? `${order.user.fname} ${order.user.lname}` : 'N/A'}</p>
              <p><strong>Email:</strong> ${order.user ? order.user.email : 'N/A'}</p>
              ${order.address ? `
                <p><strong>Phone:</strong> ${order.address.phone_number}</p>
                <p><strong>Address:</strong> ${order.address.full_address}</p>
              ` : ''}
            </div>
          </div>
          
          <table class="items-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Product Name</th>
                <th>Variant</th>
                <th>Quantity</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              ${order.order_products ? order.order_products.map(item => `
                <tr>
                  <td>
                    ${item.product && item.product.image_url ? 
                      `<img src="${item.product.image_url}" alt="${item.product.name}" class="item-image" />` :
                      `<div class="image-placeholder">No Image</div>`
                    }
                  </td>
                  <td>${item.product ? item.product.name : 'Unknown Product'}</td>
                  <td>${item.variant ? `${item.variant.color} (${item.variant_id})` : 'No variant'}</td>
                  <td class="quantity-cell">${item.quantity}</td>
                  <td></td>
                </tr>
              `).join('') : ''}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Generated on ${new Date().toLocaleString()}</p>
            <p>Mariamly Luxe </p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const printClientInvoice = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - Order #${order.order_id}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .logo {
              font-size: 24px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .invoice-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 30px;
            }
            .company-info, .customer-info {
              flex: 1;
              margin: 0 10px;
            }
            .section-title {
              font-size: 16px;
              font-weight: bold;
              margin-bottom: 10px;
              border-bottom: 1px solid #ccc;
              padding-bottom: 5px;
            }
            .items-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            .items-table th, .items-table td {
              border: 1px solid #333;
              padding: 10px;
              text-align: left;
            }
            .items-table th {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .items-table .quantity, .items-table .price, .items-table .total {
              text-align: center;
            }
            .item-image {
              width: 50px;
              height: 50px;
              object-fit: cover;
              border-radius: 4px;
            }
            .image-placeholder {
              width: 50px;
              height: 50px;
              background-color: #f0f0f0;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 4px;
              font-size: 10px;
              color: #666;
            }
            .totals-section {
              margin-top: 20px;
              display: flex;
              justify-content: flex-end;
            }
            .totals-table {
              width: 300px;
              border-collapse: collapse;
            }
            .totals-table td {
              padding: 8px 12px;
              border: 1px solid #333;
            }
            .totals-table .label {
              background-color: #f5f5f5;
              font-weight: bold;
            }
            .totals-table .total-row {
              background-color: #e8f5e8;
              font-weight: bold;
              font-size: 16px;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 12px;
              color: #666;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="logo">Mariamly Luxe</div>
            <h1>Invoice</h1>
            <p>Order #${order.order_id}</p>
          </div>
          
          <div class="invoice-info">
            <div class="company-info">
              <div class="section-title">From</div>
              <p><strong>Mariamly Luxe</strong></p>
              <p>Mariamly.Luxe</p>
              <p>Email: info@mariamlyluxe.com</p>
              <p>Website: www.mariamlyluxe.com</p>
            </div>
            
            <div class="customer-info">
              <div class="section-title">Bill To</div>
              <p><strong>${order.user ? `${order.user.fname} ${order.user.lname}` : 'N/A'}</strong></p>
              <p>${order.user ? order.user.email : 'N/A'}</p>
              ${order.address ? `
                <p>${order.address.full_address}</p>
                <p>Phone: ${order.address.phone_number}</p>
              ` : ''}
            </div>
          </div>
          
          <table class="items-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Product</th>
                <th>Variant</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${order.order_products ? order.order_products.map(item => `
                <tr>
                  <td>
                    ${item.product && item.product.image_url ? 
                      `<img src="${item.product.image_url}" alt="${item.product.name}" class="item-image" />` :
                      `<div class="image-placeholder">No Image</div>`
                    }
                  </td>
                  <td>${item.product ? item.product.name : 'Unknown Product'}</td>
                  <td>${item.variant ? item.variant.color : 'No variant'}</td>
                  <td class="quantity">${item.quantity}</td>
                  <td class="price">$${item.price}</td>
                  <td class="total">$${item.total}</td>
                </tr>
              `).join('') : ''}
            </tbody>
          </table>
          
          <div class="totals-section">
            <table class="totals-table">
              <tr>
                <td class="label">Subtotal:</td>
                <td>$${order.subtotal}</td>
              </tr>
              <tr>
                <td class="label">Shipping:</td>
                <td>$${order.shipping}</td>
              </tr>
              <tr class="total-row">
                <td class="label">Total:</td>
                <td>$${order.total}</td>
              </tr>
            </table>
          </div>
          
          <div class="footer">
            <p>Invoice Date: ${new Date(order.date_added).toLocaleDateString()}</p>
            <p>Payment Method: ${order.payment_code ? order.payment_code.charAt(0).toUpperCase() + order.payment_code.slice(1) : 'N/A'}</p>
            <p>Order Status: ${order.order_status ? order.order_status.charAt(0).toUpperCase() + order.order_status.slice(1) : 'N/A'}</p>
            <p>Generated on ${new Date().toLocaleString()}</p>
            <p>Thank you for your business!</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const getStatusBadge = (status) => {
    if (!status) {
      return (
        <span className="status-badge status-pending">
          Unknown
        </span>
      );
    }
    
    const statusClasses = {
      pending: "status-pending",
      processing: "status-processing",
      shipped: "status-shipped",
      delivered: "status-delivered",
      cancelled: "status-cancelled"
    };
    
    return (
      <span className={`status-badge ${statusClasses[status.toLowerCase()] || 'status-pending'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPaymentBadge = (paymentCode) => {
    if (!paymentCode) {
      return (
        <span className="payment-badge payment-cash">
          Unknown
        </span>
      );
    }
    
    const paymentClasses = {
      cash: "payment-cash",
      card: "payment-card",
      whish: "payment-whish"
    };
    
    return (
      <span className={`payment-badge ${paymentClasses[paymentCode.toLowerCase()] || 'payment-cash'}`}>
        {paymentCode.charAt(0).toUpperCase() + paymentCode.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-order-details">
          <div className="loading">
            <Loader size={24} strokeWidth={3} />
            Loading order details...
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!order) {
    return (
      <AdminLayout>
        <div className="admin-order-details">
          <div className="error-state">
            <h2>Order not found</h2>
            <p>The order you're looking for doesn't exist.</p>
            <button className="btn-primary" onClick={() => navigate("/admin/orders")}>
              Back to Orders
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-order-details">
        {/* Header */}
        <div className="admin-header">
          <div className="admin-title">
            <button 
              className="back-button" 
              onClick={() => navigate("/admin/orders")}
              title="Back to Orders"
            >
              <svg viewBox="0 0 24 24">
                <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
            </button>
            <div>
              <h1>Order Details #{order.order_id}</h1>
              <p>View and manage order information</p>
            </div>
          </div>
          <div className="admin-actions">
            <button 
              className="btn-secondary" 
              onClick={printPackagingList}
              title="Print Packaging List"
            >
              <svg viewBox="0 0 24 24">
                <path d="M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6v-8z" fill="none" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Packaging List
            </button>
            <button 
              className="btn-secondary" 
              onClick={printClientInvoice}
              title="Print Client Invoice"
            >
              <svg viewBox="0 0 24 24">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" fill="none" stroke="currentColor" strokeWidth="2"/>
                <polyline points="14,2 14,8 20,8" fill="none" stroke="currentColor" strokeWidth="2"/>
                <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2"/>
                <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2"/>
                <polyline points="10,9 9,9 8,9" fill="none" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Client Invoice
            </button>
            <button 
              className="btn-primary" 
              onClick={() => setStatusUpdateModal(true)}
            >
              <svg viewBox="0 0 24 24">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" fill="none" stroke="currentColor" strokeWidth="2"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" fill="none" stroke="currentColor" strokeWidth="2"/>
              </svg>
              Update Status
            </button>
          </div>
        </div>

        {/* Order Details Content */}
        <div className="order-details-content">
          <div className="order-info-grid">
            {/* Customer Information */}
            <div className="info-card">
              <h3>Customer Information</h3>
              <div className="info-content">
                <div className="info-item">
                  <span className="label">Name:</span>
                  <span className="value">
                    {order.user ? `${order.user.fname} ${order.user.lname}` : 'N/A'}
                  </span>
                </div>
                <div className="info-item">
                  <span className="label">Email:</span>
                  <span className="value">{order.user ? order.user.email : 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="label">Username:</span>
                  <span className="value">{order.user ? order.user.username : 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Order Information */}
            <div className="info-card">
              <h3>Order Information</h3>
              <div className="info-content">
                <div className="info-item">
                  <span className="label">Status:</span>
                  <span className="value">{getStatusBadge(order.order_status)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Payment Method:</span>
                  <span className="value">{getPaymentBadge(order.payment_code)}</span>
                </div>
                <div className="info-item">
                  <span className="label">Subtotal:</span>
                  <span className="value">${order.subtotal}</span>
                </div>
                <div className="info-item">
                  <span className="label">Shipping:</span>
                  <span className="value">${order.shipping}</span>
                </div>
                <div className="info-item">
                  <span className="label">Total Amount:</span>
                  <span className="value total-amount">${order.total}</span>
                </div>
                <div className="info-item">
                  <span className="label">Order Date:</span>
                  <span className="value">{new Date(order.date_added).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            {order.address && (
              <div className="info-card">
                <h3>Shipping Address</h3>
                <div className="info-content">
                  <div className="info-item">
                    <span className="label">Name:</span>
                    <span className="value">
                      {order.address.first_name} {order.address.last_name}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="label">Phone:</span>
                    <span className="value">{order.address.phone_number}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Address:</span>
                    <span className="value">{order.address.full_address}</span>
                  </div>
                  {order.address.more_details && (
                    <div className="info-item">
                      <span className="label">Additional Details:</span>
                      <span className="value">{order.address.more_details}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Order Items */}
          <div className="order-items-section">
            <h3>Order Items</h3>
            {order.order_products && order.order_products.length > 0 ? (
              <div className="items-table-container">
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Variant</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.order_products.map((orderProduct, index) => (
                      <tr key={index}>
                        <td>
                          <div className="item-info">
                            <div className="item-image">
                              {orderProduct.product && orderProduct.product.image_url ? (
                                <img 
                                  src={orderProduct.product.image_url} 
                                  alt={orderProduct.product.name}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    if (e.currentTarget.nextSibling) {
                                      e.currentTarget.nextSibling.style.display = "flex";
                                    }
                                  }}
                                />
                              ) : null}
                              <div
                                className="image-placeholder"
                                style={{ display: orderProduct.product && orderProduct.product.image_url ? "none" : "flex" }}
                              >
                                <svg viewBox="0 0 24 24">
                                  <path
                                    d="M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2l1.586-1.586a2 2 0 0 1 2.828 0L20 14m-6-6h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  />
                                </svg>
                              </div>
                            </div>
                            <div className="item-details">
                              <div className="item-name">
                                {orderProduct.product ? orderProduct.product.name : 'Unknown Product'}
                              </div>
                              <div className="item-id">
                                Product ID: {orderProduct.product_id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {orderProduct.variant ? (
                            <div className="variant-info">
                              <div className="variant-color">
                                <span 
                                  className="color-swatch" 
                                  style={{ backgroundColor: orderProduct.variant.hex_color }}
                                  title={orderProduct.variant.color}
                                ></span>
                                <span className="color-name">{orderProduct.variant.color}</span>
                              </div>
                              <div className="variant-id">
                                Variant ID: {orderProduct.variant_id}
                              </div>
                            </div>
                          ) : (
                            <div className="no-variant">
                              <span className="no-variant-text">No variant selected</span>
                            </div>
                          )}
                        </td>
                        <td className="quantity">{orderProduct.quantity}</td>
                        <td className="price">${orderProduct.price}</td>
                        <td className="total">${orderProduct.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-items">
                <p>No items found for this order.</p>
              </div>
            )}
          </div>
        </div>

        {/* Status Update Modal */}
        {statusUpdateModal && (
          <div className="modal-overlay" onClick={() => setStatusUpdateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Update Order Status</h2>
                <button className="modal-close" onClick={() => setStatusUpdateModal(false)}>
                  <svg viewBox="0 0 24 24">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              </div>

              <div className="modal-form">
                <div className="form-group">
                  <label htmlFor="newStatus">New Status</label>
                  <select
                    id="newStatus"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    <option value="">Select Status</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={() => setStatusUpdateModal(false)}>
                    Cancel
                  </button>
                  <button type="button" className="btn-primary" onClick={updateOrderStatus} disabled={isUpdatingStatus}>
                    {isUpdatingStatus ? (
                      <>
                        <Loader size={16} strokeWidth={2} />
                        Updating...
                      </>
                    ) : (
                      "Update Status"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
